import api from './axios';

export type SubscriptionPlan = 'STARTER' | 'PRO' | 'BUSINESS';
export type Country = 'TN' | 'FR' | 'DZ' | 'MA';
export type SaasInvoiceStatus = 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED' | 'CANCELLED';

export interface SubscriberSummary {
  id: number;
  raisonSociale: string;
  matriculeFiscal: string;
  email: string | null;
  plan: SubscriptionPlan;
  country: Country;
  createdAt: string;
  invoicesThisMonth: number;
  usersCount: number;
  actif?: boolean;
  deletedAt?: string | null;
}

export interface SubscriberDetail {
  id: number;
  raisonSociale: string;
  matriculeFiscal: string;
  adresse: string;
  telephone: string | null;
  email: string | null;
  plan: SubscriptionPlan;
  country: Country;
  createdAt: string;
  usersCount: number;
  actif?: boolean;
  deletedAt?: string | null;
  usageThisMonth: UsageSummary;
}

export interface UsageSummary {
  periodLabel: string;
  totals: Record<string, number>;
}

export type AssignableRole = 'ADMIN' | 'COMPTABLE' | 'COMMERCIAL';
export const ASSIGNABLE_ROLES: AssignableRole[] = ['ADMIN', 'COMPTABLE', 'COMMERCIAL'];

export interface AdminUserSummary {
  id: number;
  email: string;
  nom: string;
  prenom: string;
  role: string;
  actif: boolean;
}

export interface ImpersonationToken {
  accessToken: string;
  companyId: number;
  expiresIn: number;
}

export interface SaasInvoice {
  id: number;
  companyId: number;
  companyName: string;
  periodStart: string;
  periodEnd: string;
  plan: SubscriptionPlan;
  amountHt: number;
  amountTtc: number;
  status: SaasInvoiceStatus;
  paymentUrl: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface Metrics {
  totalSubscribers: number;
  subscribersByPlan: Record<string, number>;
  monthlyRecurringRevenue: number;
  annualRecurringRevenue: number;
  invoicesPaidThisMonth: number;
  invoicesPendingThisMonth: number;
  planChangesLast30Days: number;
}

export interface Page<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface SubscriberFilters {
  plan?: SubscriptionPlan;
  q?: string;
  createdAfter?: string;
  page?: number;
  size?: number;
}

export interface LogTail {
  file: string;
  available: boolean;
  returnedLines: number;
  lines: string[];
}

export interface LogQuery {
  limit?: number;
  level?: string;
  q?: string;
}

export interface SystemInfo {
  appName: string;
  activeProfiles: string[];
  startedAt: string;
  uptimeMillis: number;
  javaVersion: string;
  osName: string;
  availableProcessors: number;
  threadCount: number;
  heapUsedBytes: number;
  heapMaxBytes: number;
  heapCommittedBytes: number;
}

export interface HealthSummary {
  status: string;
  components: Record<string, string>;
}

export interface GtsSyncStatus {
  configured: boolean;
  bridgeEnabled: boolean;
  kafkaEnabled: boolean;
  gtsBaseUrl: string;
  gtsSsoBaseUrl: string;
  ssoConfigured: boolean;
  reachable: boolean;
  latencyMs: number | null;
  httpStatus: number | null;
  connectivityError: string | null;
  pending: number;
  published: number;
  failed: number;
  oldestPendingAt: string | null;
  lastPublishedAt: string | null;
}

export interface GtsManualSyncResult {
  requeuedFailed: number;
  backfilled: number;
  publishedNow: number;
  status: GtsSyncStatus;
}

export interface ScheduledJob {
  id: string;
  name: string;
  description: string;
  schedule: string;
  enabled: boolean;
  triggerable: boolean;
  nextRun: string | null;
}

export interface AiModelRoute {
  feature: string;
  provider: string;
  model: string;
  temperature: number | null;
  maxTokens: number | null;
  jsonMode: boolean;
}

export interface AiProviderStatus {
  name: string;
  available: boolean;
}

export interface AiModelsOverview {
  enabled: boolean;
  routes: AiModelRoute[];
  providers: AiProviderStatus[];
}

/** Télémétrie d'une fonctionnalité IA sous garde-fous (TF-066). */
export interface AiTelemetryRow {
  feature: string;
  calls: number;
  fallbacks: number;
  fallbackRatePct: number;
}

export interface UpdateAiModelPayload {
  provider: string;
  model: string;
  temperature: number | null;
  maxTokens: number | null;
  jsonMode: boolean;
}

export interface TestAiModelPayload extends UpdateAiModelPayload {
  prompt?: string;
}

export interface TestAiModelResult {
  success: boolean;
  statusCode: number;
  content: string;
  model: string;
  provider: string;
  tokensIn: number;
  tokensOut: number;
  latencyMs: number;
  error: string | null;
}

export interface AiModelOption {
  id: string;
  name: string;
  description: string | null;
  contextLength: number | null;
  promptPrice: number | null;
  completionPrice: number | null;
  modalities: string[] | null;
}

const adminApi = {
  listSubscribers: (filters: SubscriberFilters = {}) =>
    api.get<Page<SubscriberSummary>>('/admin/subscribers', { params: filters }).then((r) => r.data),

  getSubscriber: (id: number) =>
    api.get<SubscriberDetail>(`/admin/subscribers/${id}`).then((r) => r.data),

  getSubscriberUsage: (id: number, period?: string) =>
    api.get<UsageSummary>(`/admin/subscribers/${id}/usage`, { params: { period } }).then((r) => r.data),

  updateSubscriberPlan: (id: number, plan: SubscriptionPlan, reason?: string) =>
    api.put<SubscriberSummary>(`/admin/subscribers/${id}/plan`, { plan, reason }).then((r) => r.data),

  suspendSubscriber: (id: number) =>
    api.post<void>(`/admin/subscribers/${id}/suspend`).then(() => undefined),

  reactivateSubscriber: (id: number) =>
    api.post<void>(`/admin/subscribers/${id}/reactivate`).then(() => undefined),

  softDeleteSubscriber: (id: number) =>
    api.delete<void>(`/admin/subscribers/${id}`).then(() => undefined),

  listSubscriberUsers: (subscriberId: number) =>
    api.get<AdminUserSummary[]>(`/admin/subscribers/${subscriberId}/users`).then((r) => r.data),

  resetUserPassword: (userId: number) =>
    api.post<{ temporaryPassword: string }>(`/admin/users/${userId}/reset-password`).then((r) => r.data),

  updateUserRole: (userId: number, role: AssignableRole) =>
    api.put<AdminUserSummary>(`/admin/users/${userId}/role`, { role }).then((r) => r.data),

  impersonateUser: (userId: number) =>
    api.post<ImpersonationToken>(`/admin/users/${userId}/impersonate`).then((r) => r.data),

  listInvoices: (status?: SaasInvoiceStatus, page = 0, size = 20) =>
    api.get<Page<SaasInvoice>>('/admin/invoices', { params: { status, page, size } }).then((r) => r.data),

  runBillingNow: () =>
    api.post<{ created: number }>('/admin/billing/run-now').then((r) => r.data),

  getMetrics: () => api.get<Metrics>('/admin/metrics').then((r) => r.data),

  // --- Ops ---
  getLogs: (query: LogQuery = {}) =>
    api.get<LogTail>('/admin/ops/logs', { params: query }).then((r) => r.data),

  logsDownloadUrl: () => `${api.defaults.baseURL}/admin/ops/logs/download`,

  getSystemInfo: () => api.get<SystemInfo>('/admin/ops/system').then((r) => r.data),

  getHealth: () => api.get<HealthSummary>('/admin/ops/health').then((r) => r.data),

  getGtsStatus: () => api.get<GtsSyncStatus>('/admin/ops/gts').then((r) => r.data),

  triggerGtsSync: () =>
    api.post<GtsManualSyncResult>('/admin/ops/gts/sync').then((r) => r.data),

  listJobs: () => api.get<ScheduledJob[]>('/admin/ops/jobs').then((r) => r.data),

  runJob: (id: string) =>
    api.post<{ triggered: string }>(`/admin/ops/jobs/${id}/run`).then((r) => r.data),

  // --- AI models ---
  getAiModels: () => api.get<AiModelsOverview>('/admin/ai-models').then((r) => r.data),

  getAvailableModels: (provider: string) =>
    api
      .get<AiModelOption[]>('/admin/ai-models/available', { params: { provider } })
      .then((r) => r.data),

  updateAiModel: (feature: string, payload: UpdateAiModelPayload) =>
    api.put<AiModelRoute>(`/admin/ai-models/${feature}`, payload).then((r) => r.data),

  testAiModel: (payload: TestAiModelPayload) =>
    api.post<TestAiModelResult>('/admin/ai-models/test', payload).then((r) => r.data),

  // TF-066 — télémétrie de la couche garde-fous IA (appels / replis déterministes)
  getAiTelemetry: () =>
    api.get<AiTelemetryRow[]>('/ai/telemetry').then((r) => r.data ?? []),
};

export default adminApi;
